import { useCallback } from 'react';
import { useNavigation } from '@react-navigation/native';

import { type LastInteractedStore } from '../../../../context';
import { useCart } from '../../../../hooks';
import { type ProductForRestaurant } from '../../ProductDetails.types.ts';

// ─────────────────────────────────────────────────────────────────────────────

export const useProductDetailsNavigation = (params: Params) => {
  const { baseProductSlug, lastInteractedStore, productForRestaurant } = params;

  const deliveryAddressId = lastInteractedStore?.addressId;

  const navigation = useNavigation();
  const { isCartEmpty } = useCart();

  // ─── Helpers ─────────────────────────────────────────────────────────

  const navigateToMenu = useCallback(() => {
    navigation.navigate('MainTabs', {
      screen: 'MenuTab',
      params: { screen: 'MenuWithoutSlug' },
    });
  }, [navigation]);

  const navigateToLocationSearch = useCallback(() => {
    navigation.navigate('MainTabs', {
      screen: 'MenuTab',
      params: { screen: 'Locations' },
    });
  }, [navigation]);

  const navigateToProductDetailsLocation = useCallback(() => {
    navigation.navigate('Modal', {
      screen: 'ProductDetailsLocation',
      params: { baseProductSlug },
    });
  }, [baseProductSlug, navigation]);

  const navigateBack = useCallback(() => {
    if (navigation.canGoBack()) {
      navigation.goBack();

      return;
    }

    navigateToMenu();
  }, [navigateToMenu, navigation]);

  const navigateToCustomization = useCallback(() => {
    const shouldSelectLocation = !lastInteractedStore || !productForRestaurant;

    if (shouldSelectLocation) {
      navigateToProductDetailsLocation();

      return;
    }

    if (deliveryAddressId) {
      navigation.navigate('Modal', {
        screen: 'CustomizationDelivery',
        params: {
          productSlug: productForRestaurant?.slug,
          addressId: deliveryAddressId,
        },
      });

      return;
    }

    navigation.navigate('Modal', {
      screen: 'Customization',
      params: {
        productSlug: productForRestaurant?.slug,
        restaurantSlug: productForRestaurant.restaurant.slug,
      },
    });
  }, [
    deliveryAddressId,
    lastInteractedStore,
    navigateToProductDetailsLocation,
    navigation,
    productForRestaurant,
  ]);

  // ─────────────────────────────────────────────────────────────────────

  return {
    navigateBack,
    navigateToCustomization,

    // NOTE: If there is no need for reorder, we can open "inline" location search.
    navigateToLocationSearch: isCartEmpty
      ? navigateToProductDetailsLocation
      : navigateToLocationSearch,
  };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  baseProductSlug: string;
  lastInteractedStore: LastInteractedStore | undefined;
  productForRestaurant: ProductForRestaurant | null | undefined;
};
